<!-- eslint-disable -->
<template>
  <div class="kanban-wrapper">
    <div class="kanban-header">
      <h2 class="kanban-title">Kanban</h2>
      <v-btn color="primary" variant="flat" size="small" @click="openNewTask">
        <v-icon start size="small">mdi-plus</v-icon>
        {{ $t('kanban.newTask') || 'Ny opgave' }}
      </v-btn>
    </div>

    <div class="kanban-board">
      <div
        v-for="column in columns"
        :key="column.key"
        class="kanban-column"
        @dragover.prevent="onDragOver($event, column.key)"
        @dragleave="onDragLeave($event)"
        @drop="onDrop($event, column.key)"
      >
        <div class="column-header" :style="{ borderColor: column.color }">
          <div class="column-title-row">
            <span class="column-dot" :style="{ background: column.color }"></span>
            <span class="column-title">{{ column.label }}</span>
            <span class="column-count">{{ getColumnTasks(column.key).length }}</span>
          </div>
        </div>

        <div class="column-body" :data-column="column.key">
          <TransitionGroup name="card" tag="div" class="card-list">
            <div
              v-for="task in getColumnTasks(column.key)"
              :key="task.taskId"
              class="kanban-card"
              :class="{ 'dragging': draggedTask?.taskId === task.taskId }"
              draggable="true"
              @dragstart="onDragStart($event, task)"
              @dragend="onDragEnd"
              @click="openEditTask(task)"
            >
              <!-- Priority indicator -->
              <div class="card-priority" :style="{ background: getPriorityColor(task.priority) }"></div>
              
              <div class="card-content">
                <div class="card-title">{{ task.title }}</div>
                <div v-if="task.description" class="card-description">{{ task.description }}</div>
                
                <div class="card-footer">
                  <div class="card-tags">
                    <span v-if="task.priority" class="card-tag" :style="{ 
                      background: getPriorityColor(task.priority) + '18',
                      color: getPriorityColor(task.priority)
                    }">
                      {{ getPriorityLabel(task.priority) }}
                    </span>
                  </div>
                  
                  <div class="card-meta">
                    <!-- Comment count badge -->
                    <span v-if="task.commentCount > 0" class="card-comments-badge">
                      <v-icon size="10" class="mr-1">mdi-comment-outline</v-icon>
                      {{ task.commentCount }}
                    </span>
                    <v-avatar v-if="task.assignee" size="22" color="primary" class="card-avatar">
                      <v-img v-if="task.assignee.avatarUrl" :src="task.assignee.avatarUrl"></v-img>
                      <span v-else class="avatar-text">{{ task.assignee.displayName?.charAt(0) || '?' }}</span>
                    </v-avatar>
                    <span v-if="task.dueDate" class="card-due" :class="{ overdue: isOverdue(task.dueDate) }">
                      <v-icon size="10" class="mr-1">mdi-clock-outline</v-icon>
                      {{ formatDueDate(task.dueDate) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </TransitionGroup>

          <!-- Empty state -->
          <div v-if="getColumnTasks(column.key).length === 0" class="column-empty">
            <v-icon size="20" color="grey-lighten-1">mdi-card-outline</v-icon>
          </div>
        </div>
      </div>
    </div>

    <!-- Task Dialog (with comments) -->
    <v-dialog v-model="showTaskDialog" max-width="600" persistent scrollable>
      <v-card class="task-dialog">
        <v-card-title class="d-flex align-center">
          <span>{{ editingTask ? ($t('kanban.editTask') || 'Rediger opgave') : ($t('kanban.newTask') || 'Ny opgave') }}</span>
          <v-spacer />
          <v-btn v-if="editingTask" icon size="small" variant="text" color="red" @click="confirmDelete">
            <v-icon size="18">mdi-delete</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="task-dialog-content">
          <!-- Task form fields -->
          <v-text-field
            v-model="taskForm.title"
            :label="$t('kanban.title') || 'Titel'"
            variant="outlined"
            density="compact"
            class="mb-3"
            autofocus
            @keydown.enter="saveTask"
          ></v-text-field>

          <v-textarea
            v-model="taskForm.description"
            :label="$t('kanban.description') || 'Beskrivelse'"
            variant="outlined"
            density="compact"
            rows="3"
            class="mb-3"
          ></v-textarea>

          <v-row dense class="mb-3">
            <v-col cols="6">
              <v-select
                v-model="taskForm.status"
                :items="columns"
                item-title="label"
                item-value="key"
                :return-object="false"
                :label="$t('kanban.status') || 'Status'"
                variant="outlined"
                density="compact"
              ></v-select>
            </v-col>
            <v-col cols="6">
              <v-select
                v-model="taskForm.priority"
                :items="priorities"
                item-title="label"
                item-value="key"
                :return-object="false"
                :label="$t('kanban.priority') || 'Prioritet'"
                variant="outlined"
                density="compact"
                clearable
              ></v-select>
            </v-col>
          </v-row>

          <v-row dense class="mb-3">
            <v-col cols="6">
              <v-select
                v-model="taskForm.assigneeId"
                :items="users"
                item-title="displayName"
                item-value="userId"
                :label="$t('kanban.assignee') || 'Tildelt'"
                variant="outlined"
                density="compact"
                clearable
              ></v-select>
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="taskForm.dueDate"
                :label="$t('kanban.dueDate') || 'Deadline'"
                type="date"
                variant="outlined"
                density="compact"
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Comments Section (only for existing tasks) -->
          <div v-if="editingTask" class="comments-section">
            <v-divider class="mb-4" />

            <div class="comments-header">
              <v-icon size="18" class="mr-2">mdi-comment-text-outline</v-icon>
              <span class="comments-title">
                {{ $t('kanban.comments') || 'Kommentarer' }}
                <span v-if="comments.length" class="comments-count">({{ comments.length }})</span>
              </span>
            </div>

            <!-- Comments list -->
            <div v-if="loadingComments" class="text-center py-4">
              <v-progress-circular size="24" indeterminate color="primary" />
            </div>

            <div v-else-if="comments.length === 0" class="no-comments">
              <v-icon size="32" color="grey-lighten-1" class="mb-2">mdi-comment-outline</v-icon>
              <div class="text-caption text-grey">{{ $t('kanban.noComments') || 'Ingen kommentarer endnu' }}</div>
            </div>

            <div v-else class="comments-list">
              <div v-for="comment in comments" :key="comment.commentId" class="comment-item">
                <v-avatar size="28" color="primary" class="comment-avatar">
                  <v-img v-if="comment.avatarUrl" :src="comment.avatarUrl"></v-img>
                  <span v-else class="avatar-text">{{ comment.displayName?.charAt(0) || '?' }}</span>
                </v-avatar>
                <div class="comment-body">
                  <div class="comment-meta">
                    <span class="comment-author">{{ comment.displayName || comment.username }}</span>
                    <span class="comment-time">{{ formatCommentTime(comment.created_at) }}</span>
                    <v-btn
                      v-if="comment.userId === currentUserId"
                      icon
                      size="x-small"
                      variant="text"
                      class="comment-delete-btn"
                      @click.stop="deleteComment(comment)"
                    >
                      <v-icon size="12">mdi-close</v-icon>
                    </v-btn>
                  </div>
                  <div class="comment-text">{{ comment.body }}</div>
                </div>
              </div>
            </div>

            <!-- New comment input -->
            <div class="comment-input">
              <v-textarea
                v-model="newComment"
                :placeholder="$t('kanban.writeComment') || 'Skriv en kommentar...'"
                variant="outlined"
                density="compact"
                rows="2"
                hide-details
                auto-grow
                @keydown.ctrl.enter="addComment"
                @keydown.meta.enter="addComment"
              ></v-textarea>
              <v-btn
                color="primary"
                variant="flat"
                size="small"
                class="mt-2"
                :loading="savingComment"
                :disabled="!newComment?.trim()"
                @click="addComment"
              >
                <v-icon start size="14">mdi-send</v-icon>
                {{ $t('kanban.addComment') || 'Kommentér' }}
              </v-btn>
            </div>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="closeTaskDialog">{{ $t('common.cancel') || 'Annuller' }}</v-btn>
          <v-btn color="primary" variant="flat" @click="saveTask" :loading="saving" :disabled="!taskForm.title?.trim()">
            {{ $t('common.save') || 'Gem' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete confirmation -->
    <v-dialog v-model="showDeleteDialog" max-width="360">
      <v-card>
        <v-card-title>{{ $t('kanban.deleteTask') || 'Slet opgave' }}</v-card-title>
        <v-card-text>{{ $t('kanban.deleteConfirm') || 'Er du sikker på at du vil slette denne opgave?' }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="showDeleteDialog = false">{{ $t('common.cancel') || 'Annuller' }}</v-btn>
          <v-btn color="red" variant="flat" @click="deleteTask" :loading="saving">{{ $t('common.delete') || 'Slet' }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
/* eslint-disable */
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
// import { useStore } from 'vuex'
import api from '../../services/api'

const { t } = useI18n()
// const store = useStore()

const props = defineProps({
  users: { type: Array, default: () => [] }
})

// Current user ID for comment ownership
const currentUserId = computed(() => {
  try {
    const user = JSON.parse(localStorage.getItem('user'))
    return user?.userId || null
  } catch {
    return null
  }
})

// ─── COLUMNS ───────────────────────────────────────────

const columns = computed(() => [
  { key: 'ideas',       label: t('kanban.columns.ideas', 'Ideas'),             color: '#9C27B0' },
  { key: 'todo',        label: t('kanban.columns.todo', 'ToDo'),               color: '#2196F3' },
  { key: 'in_progress', label: t('kanban.columns.inProgress', 'In Progress'),  color: '#FF9800' },
  { key: 'test',        label: t('kanban.columns.test', 'Test'),               color: '#00BCD4' },
  { key: 'done',        label: t('kanban.columns.done', 'Done'),               color: '#4CAF50' },
])

const priorities = computed(() => [
  { key: 'low',    label: t('kanban.priorities.low', 'Low'),       color: '#8BC34A' },
  { key: 'medium', label: t('kanban.priorities.medium', 'Medium'), color: '#FF9800' },
  { key: 'high',   label: t('kanban.priorities.high', 'High'),     color: '#F44336' },
  { key: 'urgent', label: t('kanban.priorities.urgent', 'Urgent'), color: '#9C27B0' },
])

const getPriorityColor = (key) => {
  return priorities.value.find(p => p.key === key)?.color || '#9E9E9E'
}

const getPriorityLabel = (key) => {
  return priorities.value.find(p => p.key === key)?.label || key
}

// ─── STATE ─────────────────────────────────────────────

const tasks = ref([])
const loading = ref(true)
const saving = ref(false)
const showTaskDialog = ref(false)
const showDeleteDialog = ref(false)
const editingTask = ref(null)
const draggedTask = ref(null)

// Comment state
const comments = ref([])
const newComment = ref('')
const loadingComments = ref(false)
const savingComment = ref(false)

const taskForm = ref({
  title: '',
  description: '',
  status: 'todo',
  priority: null,
  assigneeId: null,
  dueDate: null,
})

// ─── COMPUTED ──────────────────────────────────────────

const getColumnTasks = (columnKey) => {
  return tasks.value
    .filter(t => t.status === columnKey)
    .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
}

// ─── DRAG & DROP ───────────────────────────────────────

const onDragStart = (event, task) => {
  draggedTask.value = task
  event.dataTransfer.effectAllowed = 'move'
  event.dataTransfer.setData('text/plain', task.taskId)
  event.target.style.opacity = '0.5'
}

const onDragEnd = (event) => {
  event.target.style.opacity = '1'
  draggedTask.value = null
  document.querySelectorAll('.drag-over').forEach(el => el.classList.remove('drag-over'))
}

const onDragOver = (event, columnKey) => {
  event.preventDefault()
  event.dataTransfer.dropEffect = 'move'
  const column = event.currentTarget.querySelector('.column-body')
  if (column) column.classList.add('drag-over')
}

const onDragLeave = (event) => {
  const column = event.currentTarget.querySelector('.column-body')
  if (column && !event.currentTarget.contains(event.relatedTarget)) {
    column.classList.remove('drag-over')
  }
}

const onDrop = async (event, columnKey) => {
  event.preventDefault()
  const column = event.currentTarget.querySelector('.column-body')
  if (column) column.classList.remove('drag-over')

  if (!draggedTask.value) return
  if (draggedTask.value.status === columnKey) return

  const oldStatus = draggedTask.value.status
  draggedTask.value.status = columnKey

  try {
    await api.put(`/admin/kanban/tasks/${draggedTask.value.taskId}`, {
      status: columnKey
    })
  } catch (error) {
    console.error('Error moving task:', error)
    draggedTask.value.status = oldStatus
  }

  draggedTask.value = null
}

// ─── TASK CRUD ─────────────────────────────────────────

const loadTasks = async () => {
  loading.value = true
  try {
    const response = await api.get('/admin/kanban/tasks')
    tasks.value = response.data.tasks || []
  } catch (error) {
    console.error('Error loading tasks:', error)
  } finally {
    loading.value = false
  }
}

const openNewTask = (status = 'todo') => {
  editingTask.value = null
  comments.value = []
  newComment.value = ''
  taskForm.value = {
    title: '',
    description: '',
    status: typeof status === 'string' ? status : 'todo',
    priority: null,
    assigneeId: null,
    dueDate: null,
  }
  showTaskDialog.value = true
}

const openEditTask = (task) => {
  editingTask.value = task
  newComment.value = ''
  taskForm.value = {
    title: task.title,
    description: task.description || '',
    status: task.status,
    priority: task.priority || null,
    assigneeId: task.assignee?.userId || null,
    dueDate: task.dueDate ? task.dueDate.substring(0, 10) : null,
  }
  showTaskDialog.value = true
  loadComments(task.taskId)
}

const closeTaskDialog = () => {
  showTaskDialog.value = false
  editingTask.value = null
  comments.value = []
  newComment.value = ''
}

const saveTask = async () => {
  if (!taskForm.value.title?.trim()) return
  saving.value = true

  try {
    if (editingTask.value) {
      const response = await api.put(`/admin/kanban/tasks/${editingTask.value.taskId}`, taskForm.value)
      const idx = tasks.value.findIndex(t => t.taskId === editingTask.value.taskId)
      if (idx !== -1 && response.data.task) {
        tasks.value[idx] = response.data.task
      }
    } else {
      const response = await api.post('/admin/kanban/tasks', taskForm.value)
      if (response.data.task) {
        tasks.value.push(response.data.task)
      }
    }
    closeTaskDialog()
  } catch (error) {
    console.error('Error saving task:', error)
  } finally {
    saving.value = false
  }
}

const confirmDelete = () => {
  showDeleteDialog.value = true
}

const deleteTask = async () => {
  if (!editingTask.value) return
  saving.value = true

  try {
    await api.delete(`/admin/kanban/tasks/${editingTask.value.taskId}`)
    tasks.value = tasks.value.filter(t => t.taskId !== editingTask.value.taskId)
    showDeleteDialog.value = false
    closeTaskDialog()
  } catch (error) {
    console.error('Error deleting task:', error)
  } finally {
    saving.value = false
  }
}

// ─── COMMENTS ──────────────────────────────────────────

const loadComments = async (taskId) => {
  loadingComments.value = true
  comments.value = []
  try {
    const response = await api.get(`/admin/kanban/tasks/${taskId}/comments`)
    comments.value = response.data.comments || []
  } catch (error) {
    console.error('Error loading comments:', error)
  } finally {
    loadingComments.value = false
  }
}

const addComment = async () => {
  if (!newComment.value?.trim() || !editingTask.value) return
  savingComment.value = true

  try {
    const response = await api.post(`/admin/kanban/tasks/${editingTask.value.taskId}/comments`, {
      body: newComment.value.trim()
    })

    if (response.data.comment) {
      comments.value.push(response.data.comment)
      newComment.value = ''

      // Update comment count on the task card
      const taskIdx = tasks.value.findIndex(t => t.taskId === editingTask.value.taskId)
      if (taskIdx !== -1) {
        tasks.value[taskIdx].commentCount = (tasks.value[taskIdx].commentCount || 0) + 1
      }
    }
  } catch (error) {
    console.error('Error adding comment:', error)
  } finally {
    savingComment.value = false
  }
}

const deleteComment = async (comment) => {
  if (!editingTask.value) return

  try {
    await api.delete(`/admin/kanban/tasks/${editingTask.value.taskId}/comments/${comment.commentId}`)
    comments.value = comments.value.filter(c => c.commentId !== comment.commentId)

    // Update comment count
    const taskIdx = tasks.value.findIndex(t => t.taskId === editingTask.value.taskId)
    if (taskIdx !== -1 && tasks.value[taskIdx].commentCount > 0) {
      tasks.value[taskIdx].commentCount -= 1
    }
  } catch (error) {
    console.error('Error deleting comment:', error)
  }
}

const formatCommentTime = (dateStr) => {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now - date
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return t('kanban.justNow', 'Lige nu')
  if (diffMins < 60) return t('kanban.minutesAgo', '{n} min siden', { n: diffMins })
  if (diffHours < 24) return t('kanban.hoursAgo', '{n} timer siden', { n: diffHours })
  if (diffDays < 7) return t('kanban.daysAgo', '{n} dage siden', { n: diffDays })

  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}

// ─── HELPERS ───────────────────────────────────────────

const formatDueDate = (dateStr) => {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  const now = new Date()
  const diffDays = Math.floor((date - now) / (1000 * 60 * 60 * 24))
  
  if (diffDays === 0) return t('kanban.today', 'Today')
  if (diffDays === 1) return t('kanban.tomorrow', 'Tomorrow')
  if (diffDays === -1) return t('kanban.yesterday', 'Yesterday')
  
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
}

const isOverdue = (dateStr) => {
  if (!dateStr) return false
  return new Date(dateStr) < new Date(new Date().toDateString())
}

// ─── INIT ──────────────────────────────────────────────

onMounted(() => {
  loadTasks()
})
</script>

<style scoped>
.kanban-wrapper {
  padding: 0;
}

.kanban-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.kanban-title {
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.3px;
  color: #1a1a1a;
}

.kanban-board {
  display: flex;
  gap: 14px;
  overflow-x: auto;
  padding-bottom: 16px;
  min-height: 500px;
}

.kanban-column {
  flex: 1;
  min-width: 220px;
  max-width: 300px;
  display: flex;
  flex-direction: column;
}

.column-header {
  padding: 10px 12px;
  border-top: 3px solid;
  background: #fafafa;
  border-radius: 8px 8px 0 0;
}

.column-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.column-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.column-title {
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #555;
}

.column-count {
  margin-left: auto;
  font-size: 11px;
  font-weight: 600;
  color: #999;
  background: #eee;
  padding: 1px 7px;
  border-radius: 10px;
}

.column-body {
  flex: 1;
  background: #f5f5f5;
  border-radius: 0 0 8px 8px;
  padding: 8px;
  min-height: 100px;
  transition: background 0.2s;
}

.column-body.drag-over {
  background: #e3f2fd;
  outline: 2px dashed #90CAF9;
  outline-offset: -2px;
}

.card-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.column-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  opacity: 0.4;
}

/* ─── CARDS ─────────────────────────────────────────── */

.kanban-card {
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  cursor: grab;
  transition: box-shadow 0.2s, transform 0.15s;
  display: flex;
  overflow: hidden;
}

.kanban-card:hover {
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.12);
  transform: translateY(-1px);
}

.kanban-card:active {
  cursor: grabbing;
}

.kanban-card.dragging {
  opacity: 0.5;
  transform: rotate(2deg);
}

.card-priority {
  width: 4px;
  flex-shrink: 0;
}

.card-content {
  flex: 1;
  padding: 10px 12px;
  min-width: 0;
}

.card-title {
  font-size: 13px;
  font-weight: 600;
  color: #1a1a1a;
  line-height: 1.3;
  word-wrap: break-word;
}

.card-description {
  font-size: 12px;
  color: #777;
  margin-top: 4px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
  gap: 6px;
}

.card-tags {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.card-tag {
  font-size: 10px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.card-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
}

.card-comments {
  font-size: 10px;
  color: #999;
  display: flex;
  align-items: center;
}

.card-comments-badge {
  font-size: 11px;
  font-weight: 600;
  color: #4A8B7F;
  background: rgba(74, 139, 127, 0.12);
  padding: 1px 6px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 2px;
}

.card-avatar {
  flex-shrink: 0;
}

.avatar-text {
  font-size: 10px;
  font-weight: 600;
  color: white;
}

.card-due {
  font-size: 10px;
  color: #999;
  display: flex;
  align-items: center;
  white-space: nowrap;
}

.card-due.overdue {
  color: #F44336;
  font-weight: 600;
}

/* ─── TRANSITIONS ───────────────────────────────────── */

.card-move,
.card-enter-active,
.card-leave-active {
  transition: all 0.3s ease;
}

.card-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.card-leave-to {
  opacity: 0;
  transform: translateX(20px);
}

.card-leave-active {
  position: absolute;
}

/* ─── DIALOG ────────────────────────────────────────── */

.task-dialog {
  border-radius: 12px !important;
}

.task-dialog-content {
  max-height: 70vh;
  overflow-y: auto;
}

/* ─── COMMENTS ──────────────────────────────────────── */

.comments-section {
  margin-top: 8px;
}

.comments-header {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.comments-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.comments-count {
  font-weight: 400;
  color: #999;
}

.no-comments {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 0;
}

.comments-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
}

.comment-item {
  display: flex;
  gap: 10px;
}

.comment-avatar {
  flex-shrink: 0;
  margin-top: 2px;
}

.comment-body {
  flex: 1;
  min-width: 0;
  background: #f5f5f5;
  border-radius: 8px;
  padding: 8px 12px;
}

.comment-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 2px;
}

.comment-author {
  font-size: 12px;
  font-weight: 600;
  color: #333;
}

.comment-time {
  font-size: 11px;
  color: #999;
}

.comment-delete-btn {
  margin-left: auto;
  opacity: 0;
  transition: opacity 0.15s;
}

.comment-item:hover .comment-delete-btn {
  opacity: 0.6;
}

.comment-delete-btn:hover {
  opacity: 1 !important;
}

.comment-text {
  font-size: 13px;
  color: #444;
  line-height: 1.5;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.comment-input {
  margin-top: 12px;
}

/* ─── RESPONSIVE ────────────────────────────────────── */

@media (max-width: 960px) {
  .kanban-board {
    gap: 10px;
  }
  .kanban-column {
    min-width: 180px;
  }
}
</style>